<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    :transition="false"
    persistent
    ref="dialog"
    width="561px"
    :value="!!isOpen"
    absolute
  >
    <div class="card-overlay" @click="$refs.dialog.animateClick()"/>
    <v-card style="width: 561px" v-if="isOpen">
      <template v-if="selectedTheme !== undefined && Object.keys(selectedTheme).length && selectedTheme.screenshot_url">
        <v-img
          height="285px"
          width="100%"
          position="top center"
          :src="selectedTheme.screenshot_url"
        />
        <v-divider/>
      </template>
      <v-card-text height="800px" class="px-10 pt-8">
        <v-btn x-small icon @click="$emit('remove-modal')" class="close-model">
          <v-icon>$close</v-icon>
        </v-btn>

        <v-form ref="form">
          <h6>{{ $t(`heading.onboarding.newInstance.${userConfigurableOptionName}.subtitle`) }}</h6>
          <p class="p-3 mt-2">
            {{ $t(`heading.onboarding.newInstance.${userConfigurableOptionName}.info`) }}
          </p>

          <div
            v-for="(option, index) in userConfigurableOptions"
            :key="index"
          >
            <label class="base--text p-3 mb-2">{{ $t(`form.label.${option.name}`) }}</label>
            <v-select
              v-if="option.type === 'select'"
              v-model="selectedUserConfigurableOptions[option.name]"
              item-value="value"
              item-text="text"
              :items="userConfigurableOptionValues[option.name]"
              outlined
              dense
              required
              :menu-props="{
                offsetY: true,
              }"
              content
              class="mb-4 mt-2"
              :loading="loadingUserConfigurableOptionValues"
            >
              <template v-slot:no-data>
                <loader/>
              </template>
            </v-select>
            <v-text-field
              v-else
              outlined
              :placeholder="$t('form.placeholder.domain')"
              v-model="selectedUserConfigurableOptions[option.name]"
              hide-details=""
              class="v-input--lg"
              large
            />
          </div>

        </v-form>
      </v-card-text>
      <template v-if="isImport">
        <v-card-actions class="pt-0 d-flex align-center justify-space-between">
          <div>
            <step-indicator :currentStep="maxSteps === 6 ? 4 : 3" :maxSteps="maxSteps"/>
          </div>
          <div class="d-flex align-center">
            <v-btn
              large
              @click="$emit('remove-modal')"
              text
              elevation="0"
              class="mr-3"
            >
              <span class="font-weight-bold gray--text text--darken-1">
                {{ $t('button.back') }}
              </span>
            </v-btn>
            <v-btn
              large
              @click="selectUserConfigurableOptions"
              color="primary"
              elevation="0"
            >
              <span class="font-weight-bold">
                <span>{{ $t('button.continue') }}</span>
                <v-icon size="20" class="mr-0">$arrowright</v-icon>
              </span>
            </v-btn>
          </div>
        </v-card-actions>
      </template>
      <template v-else>
        <v-card-actions class="d-flex flex-column pb-4 px-10 pt-2">
          <v-btn
            x-large
            elevation="0"
            color="primary"
            block
            @click="selectUserConfigurableOptions"
            :loading="loading"
          >
            <template v-slot:loader>
              <v-progress-circular
                indeterminate
                size="20"
                width="2"
                class="mr-2"
              ></v-progress-circular>
              <span>{{ $t("button.domain.creating") }}</span>
            </template>

            <template v-if="hasSelectedServiceAskForDomain()">
              {{ $t("button.domain.set") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>

            <template v-else-if="hasSelectedServiceAskForSiteName()">
              {{ $t("button.website.set") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>

            <template v-else>
              {{ $t("button.website.create") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
          </v-btn>
          <v-btn
            v-if="services.length > 1"
            @click="$emit('change-service-button-clicked')"
            x-large
            elevation="0"
            class="mx-0 mt-2"
            color="gray"
            text
            block
          >
            <span class="font-weight-light gray--text text--darken-1">
              {{ $t("button.service.change") }}
            </span>
          </v-btn>
          <v-btn
            v-else-if="canSelectedServiceChooseTheme()"
            @click="$emit('change-theme-button-clicked')"
            x-large
            elevation="0"
            class="mx-0 mt-2"
            color="gray"
            text
            block
          >
            <span class="font-weight-light gray--text text--darken-1">
              {{ $t("button.theme.change") }}
            </span>
          </v-btn>
          <v-btn
            v-else
            @click="$emit('remove-modal')"
            x-large
            elevation="0"
            class="mx-0 mt-2"
            color="gray"
            text
            block
          >
            <span class="font-weight-light gray--text text--darken-1">
              {{ $t("button.cancel") }}
            </span>
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import StepIndicator from "../StepIndicator.vue";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import Loader from "../Loader.vue";
import Api from "../../apis/Api";
import OnboardingSelectedServiceMixin from "../../mixins/OnboardingSelectedServiceMixin";

export default {
  name: "OnboardingChooseUserConfigurableOptionsModal",
  components: {Loader, StepIndicator},
  mixins: [FormRulesMixin, OnboardingSelectedServiceMixin],
  props: {
    selectedService: Object,
    selectedTheme: Object,
    services: Array,
    isOpen: [Boolean, String],
    loading: Boolean,
    isImport: Boolean,
    maxSteps: Number,
  },
  data() {
    return {
      selectedUserConfigurableOptions: {},
      loadingUserConfigurableOptionValues: false,
      userConfigurableOptionValues: []
    }
  },
  computed: {
    userConfigurableOptions() {
      return this.selectedService.onboarding.user_configurable_options;
    },
    userConfigurableOptionName() {
      if (this.selectedService.onboarding.user_configurable_options.length > 0) {
        return this.selectedService.onboarding.user_configurable_options[0].name;
      }
      return null;
    }
  },
  methods: {
    loadUserConfigurableOptions() {
      this.loadingUserConfigurableOptionValues = true;
      Api.cached().get(`/services/${this.selectedService.id}/user-configurable-options`)
        .then(response => {
          this.userConfigurableOptionValues = response.data.data;
          this.selectedService.onboarding.user_configurable_options.forEach((option) => {
            this.selectedUserConfigurableOptions[option.name] = response.data.data[option.name][0].value;
          })
        }
      ).finally(() => {
        this.loadingUserConfigurableOptionValues = false;
      })
    },
    selectUserConfigurableOptions() {
      this.$emit('user-configurable-options-selected', this.selectedUserConfigurableOptions);
      this.selectedUserConfigurableOptions = {};
      this.userConfigurableOptionValues = [];
    }
  },
  watch: {
    isOpen: function (open) {
      if (open) {
        this.$store.dispatch("lockBodyScroll");
        this.loadUserConfigurableOptions();
      }
      this.$store.dispatch("unlockBodyScroll");
    },
  },
}
</script>